import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink } from 'lucide-react';
import { getBcGlEntriesUrl } from '../../utils/bcLinks';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { ColumnFilter } from '../ui/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import { useVatLang } from './VatLang';
import type { GlAccountMovement, VatCompany } from '../../types/vatReporting';

/**
 * The postings behind a G/L reconciliation check - one account, or every account a check like
 * GL_RECONCILIATION_INPUT rolled up (dbi's input-tax check alone covers three).
 *
 * A modal rather than an inline accordion: account 2202 carries 255 documents in a single
 * month, and unfolding that inside the reconciliation table pushes every other account off the
 * screen. Same shell, same table standard - sticky header, chevron sort, drag-resizable columns,
 * now also the Excel-style column filter - as every other drill-down on this page.
 *
 * The rows come from the payload the return already loaded, so what the modal shows and what
 * the check total says cannot drift apart.
 */

interface Props {
    movements: GlAccountMovement[];
    company: VatCompany;
    onClose: () => void;
}

const money = (v: number) =>
    new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

type ColKey = 'documentNo' | 'glAccountNo' | 'entries' | 'amount' | 'kind';

interface Row {
    documentNo: string;
    amount: number;
    entries: number;
    isSettlement: boolean;
    glAccountNo: string;
    glAccountName: string | null;
}

const ACCESSOR: Record<ColKey, (r: Row) => string | number> = {
    documentNo: r => r.documentNo,
    glAccountNo: r => r.glAccountNo,
    entries: r => r.entries,
    amount: r => r.amount,
    kind: r => (r.isSettlement ? 1 : 0),
};

const NUMERIC: ColKey[] = ['entries', 'amount'];

export function GlPostingsModal({ movements, company, onClose }: Props) {
    const { t } = useVatLang();
    const [sort, setSort] = useState<SortState<ColKey>>({ key: 'documentNo', direction: 'asc' });

    const multiAccount = movements.length > 1;
    const columns: { key: ColKey; labelKey: string; width: number; align?: 'right' }[] = [
        { key: 'documentNo', labelKey: 'drill.document', width: 220 },
        ...(multiAccount ? [{ key: 'glAccountNo' as const, labelKey: 'gl.account', width: 100 }] : []),
        { key: 'kind', labelKey: 'gl.kind', width: 150 },
        { key: 'entries', labelKey: 'gl.postings', width: 110, align: 'right' as const },
        { key: 'amount', labelKey: 'gl.amountChf', width: 150, align: 'right' as const },
    ];

    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(columns, 'vat-gl-postings-col-widths');

    const allRows: Row[] = movements.flatMap(m => m.documents.map(d => ({
        ...d, glAccountNo: m.glAccountNo, glAccountName: m.glAccountName,
    })));

    const filterColumns = columns.map(c => ({ key: c.key, accessor: ACCESSOR[c.key], numeric: NUMERIC.includes(c.key) }));
    const { filtered, filters, setColumnFilter, isFiltered, activeCount, clearAll, uniqueValuesFor } =
        useColumnFilters(allRows, filterColumns);
    const filterLabels = {
        search: t('filter.search'), all: t('filter.all'), none: t('filter.none'),
        from: t('filter.from'), to: t('filter.to'), empty: t('filter.empty'),
    };

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const onSort = (key: ColKey) => {
        // A drag ends with a mouseup the browser reports as a click; without this every resize
        // would also re-sort.
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, NUMERIC.includes(key)));
    };

    const rows = [...filtered].sort((a, b) => {
        const acc = ACCESSOR[sort.key as ColKey];
        const av = acc(a);
        const bv = acc(b);
        const base = typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av).localeCompare(String(bv), undefined, { numeric: true });
        return base * (sort.direction === 'asc' ? 1 : -1);
    });

    const operations = rows.filter(d => !d.isSettlement).reduce((s, d) => s + d.amount, 0);
    const operationsEntries = rows.filter(d => !d.isSettlement).reduce((s, d) => s + d.entries, 0);
    const totalOperationsMovement = movements.reduce((s, m) => s + m.operationsMovement, 0);
    const totalPostingCount = movements.reduce((s, m) => s + m.operationsEntries + m.settlementEntries, 0);

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
                 onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between px-5 py-3 border-b">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">
                            {movements.map(m => m.glAccountNo).join(', ')}
                            {' · '}
                            {movements.map(m => m.glAccountName ?? m.glAccountNo).join(' / ')}
                        </h2>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {t('gl.postingCount', { count: totalPostingCount })}
                            {' · '}
                            {t('gl.operations')} {money(totalOperationsMovement)}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {activeCount > 0 && (
                    <div className="flex items-center justify-between px-5 py-1.5 border-b bg-amber-50 text-xs text-amber-900">
                        <span>{t('filter.active', { count: activeCount })}</span>
                        <button onClick={clearAll} className="text-blue-600 hover:text-blue-800 font-medium">
                            {t('filter.clearAll')}
                        </button>
                    </div>
                )}

                {/* Bounded scroll container - the sticky header needs one to stick to. */}
                <div className="flex-1 overflow-auto">
                    <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                            {columns.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                        </colgroup>
                        <thead>
                            <tr className="border-b">
                                {columns.map(c => (
                                    <SortableHeader
                                        key={c.key}
                                        label={t(c.labelKey)}
                                        sortKey={c.key}
                                        sort={sort}
                                        onSort={onSort}
                                        align={c.align}
                                        onResizeStart={x => startResize(c.key, x)}
                                        onResetWidth={() => resetWidth(c.key)}
                                        isResizing={resizingKey === c.key}
                                        filter={
                                            <ColumnFilter
                                                label={t(c.labelKey)}
                                                values={uniqueValuesFor(c.key)}
                                                state={filters[c.key]}
                                                onChange={s => setColumnFilter(c.key, s)}
                                                numeric={NUMERIC.includes(c.key)}
                                                active={isFiltered(c.key)}
                                                labels={filterLabels}
                                            />
                                        }
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map((d, i) => {
                                const url = getBcGlEntriesUrl(company, d.documentNo);
                                return (
                                    <tr key={`${d.glAccountNo}|${d.documentNo}|${d.isSettlement}|${i}`}
                                        className={d.isSettlement ? 'bg-amber-50' : 'hover:bg-gray-50'}>
                                        <td className="px-3 py-1.5 font-medium truncate"
                                            onClick={e => e.stopPropagation()}>
                                            {url ? (
                                                <a href={url} target="_blank" rel="noopener noreferrer"
                                                   title={t('gl.openInBc')}
                                                   className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800">
                                                    {d.documentNo || t('gl.noDocumentNo')}
                                                    <ExternalLink className="h-3 w-3 shrink-0" />
                                                </a>
                                            ) : (d.documentNo || t('gl.noDocumentNo'))}
                                        </td>
                                        {multiAccount && (
                                            <td className="px-3 py-1.5 font-mono text-xs text-gray-600 truncate">
                                                {d.glAccountNo}
                                            </td>
                                        )}
                                        <td className="px-3 py-1.5 text-xs text-gray-600 truncate">
                                            {d.isSettlement ? t('gl.settlementExcluded') : ''}
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                                            {d.entries}
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{money(d.amount)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-gray-50 border-t font-medium">
                            <tr>
                                <td className="px-3 py-2" colSpan={multiAccount ? 3 : 2}>
                                    {activeCount > 0
                                        ? t('drill.filteredCount', { shown: filtered.length, total: allRows.length })
                                        : t('gl.operations')}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{operationsEntries}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{money(operations)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>,
        document.body
    );
}
